import { useMemo } from "react";
import { Table } from "@tanstack/react-table";
import { ListFilters } from "@/types/list";

/**
 * Adapter exposing a TanStack `Table`'s column filters as the generic
 * `ListFilters` helper (`getFilter`/`setFilter`/`clearFilter`) consumed by
 * shared filter components. Empty/null/undefined values clear the entry
 * instead of leaving an empty filter behind.
 */
export const useTableFilters = <TData>(table: Table<TData>): ListFilters =>
	useMemo(
		() => ({
			getFilter: (id: string) => {
				const columnFilters = table.options.meta?.tableState?.columnFilters ?? table.getState().columnFilters;
				return columnFilters.find(filter => filter.id === id)?.value;
			},
			setFilter: (id: string, value: unknown) => {
				table.setColumnFilters(prev => {
					const next = [...prev];
					const idx = next.findIndex(filter => filter.id === id);

					if (value === undefined || value === null || value === "") {
						if (idx >= 0) next.splice(idx, 1);
					} else if (idx >= 0) {
						next[idx] = { id, value };
					} else {
						next.push({ id, value });
					}

					return next;
				});
			},
			clearFilter: (id: string) => {
				table.setColumnFilters(prev => prev.filter(filter => filter.id !== id));
			},
		}),
		[table],
	);
